import {
  BIG_JOKER, MAX_PIP, MIN_PIP, RANK_A, SMALL_JOKER,
  isWildcard, rankOf, singleOrder, suitOf,
  type Card, type Rank, type Suit,
} from "./cards.js";
import { ComboType, beats, type Combo } from "./combos.js";
import type { Ruleset } from "./ruleset.js";

const BOMB_POWER: Record<number, number> = {
  4: 40, 5: 50, 6: 60, 7: 70, 8: 80, 9: 90, 10: 100,
};

interface HandIndex {
  /** Non-wildcard cards by rank, each group ordered by suit. */
  readonly byRank: (Card[] | undefined)[];
  /** Non-wildcard cards by suit then rank — only pips, for straight flushes. */
  readonly bySuit: (Card | undefined)[][];
  /** Count of non-wildcard cards at each rank. */
  readonly avail: Int8Array;
  readonly wilds: Card[];
  readonly ranks: number[];
}

function indexHand(hand: readonly Card[], level: Rank): HandIndex {
  const byRank: (Card[] | undefined)[] = new Array(BIG_JOKER + 1);
  const bySuit: (Card | undefined)[][] = [[], [], [], []];
  const avail = new Int8Array(BIG_JOKER + 1);
  const wilds: Card[] = [];

  for (const card of hand) {
    if (isWildcard(card, level)) { wilds.push(card); continue; }
    const rank = rankOf(card);
    const group = byRank[rank];
    if (group === undefined) byRank[rank] = [card];
    else group.push(card);
    avail[rank] = (avail[rank] as number) + 1;
    if (rank <= MAX_PIP) {
      const lane = bySuit[suitOf(card)] as (Card | undefined)[];
      if (lane[rank] === undefined) lane[rank] = card;
    }
  }

  const ranks: number[] = [];
  for (let r = MIN_PIP; r <= BIG_JOKER; r++) {
    const group = byRank[r];
    if (group === undefined) continue;
    group.sort((a, b) => suitOf(a) - suitOf(b));
    ranks.push(r);
  }
  return { byRank, bySuit, avail, wilds, ranks };
}

/** Take `count` cards of `rank`, spending wildcards only on the shortfall. */
function take(index: HandIndex, rank: number, count: number, out: Card[], wildsUsed: number): number {
  const pool = index.byRank[rank] ?? [];
  const natural = Math.min(pool.length, count);
  for (let i = 0; i < natural; i++) out.push(pool[i] as Card);
  const short = count - natural;
  for (let i = 0; i < short; i++) out.push(index.wilds[wildsUsed + i] as Card);
  return wildsUsed + short;
}

function consecutive(start: number, len: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < len; i++) out.push(start + i);
  return out;
}

function runTargets(len: number, aceLow: boolean): number[][] {
  const out: number[][] = [];
  for (let start = MIN_PIP; start + len - 1 <= MAX_PIP; start++) out.push(consecutive(start, len));
  if (aceLow && len >= 2) out.push([RANK_A, ...consecutive(MIN_PIP, len - 1)]);
  return out;
}

const RUN_CACHE = new Map<string, number[][]>();
function runs(len: number, aceLow: boolean): number[][] {
  const key = `${len}:${aceLow}`;
  let cached = RUN_CACHE.get(key);
  if (cached === undefined) { cached = runTargets(len, aceLow); RUN_CACHE.set(key, cached); }
  return cached;
}

/**
 * Every distinct combo that can be built from `hand`.
 *
 * Combos are constructed directly from the shape being targeted rather than
 * built-then-classified: the enumerator already knows it asked for "a straight
 * topped by nine", so round-tripping through the classifier is pure cost on the
 * hottest path in the AI. `enumerate.test.ts` checks the two agree on every
 * combo this produces, which is what keeps the shortcut honest.
 *
 * One representative card set per shape: if a hand holds two threes, straights
 * needing a three get the lower-suited one.
 */
export function enumerateCombos(hand: readonly Card[], level: Rank, rules: Ruleset): Combo[] {
  const index = indexHand(hand, level);
  const { avail, wilds } = index;
  const wildCount = wilds.length;
  const out: Combo[] = [];

  const emit = (type: ComboType, cards: Card[], rank: number, power: number) => {
    out.push({ type, cards, rank, length: cards.length, power });
  };

  // Singles — one per distinct rank, plus a wildcard played as a level card.
  for (const rank of index.ranks) {
    const group = index.byRank[rank];
    if (group?.[0] !== undefined) emit(ComboType.Single, [group[0]], singleOrder(rank, level), 0);
  }
  if (wildCount > 0) emit(ComboType.Single, [wilds[0] as Card], singleOrder(level, level), 0);

  // Pairs, triples and n-of-a-kind bombs, all pinned by a natural rank.
  for (const rank of index.ranks) {
    const have = avail[rank] as number;
    const isPip = rank <= MAX_PIP;
    const spare = isPip ? wildCount : 0;

    if (have + spare >= 2) {
      const cards: Card[] = [];
      take(index, rank, 2, cards, 0);
      emit(ComboType.Pair, cards, singleOrder(rank, level), 0);
    }
    if (isPip && have + spare >= 3) {
      const cards: Card[] = [];
      take(index, rank, 3, cards, 0);
      emit(ComboType.Triple, cards, singleOrder(rank, level), 0);
    }
    if (!isPip) continue;
    const bombRank = rules.levelElevatesInBombs ? singleOrder(rank, level) : rank;
    for (let size = 4; size <= have + spare; size++) {
      const power = BOMB_POWER[size];
      if (power === undefined) break;
      const cards: Card[] = [];
      take(index, rank, size, cards, 0);
      emit(ComboType.Bomb, cards, bombRank, power);
    }
  }
  if (wildCount >= 2) {
    emit(ComboType.Pair, [wilds[0] as Card, wilds[1] as Card], singleOrder(level, level), 0);
  }

  // 四大天王.
  const smalls = index.byRank[SMALL_JOKER] ?? [];
  const bigs = index.byRank[BIG_JOKER] ?? [];
  if (smalls.length === 2 && bigs.length === 2) {
    emit(ComboType.JokerBomb, [...smalls, ...bigs], BIG_JOKER, 1000);
  }

  // Full houses — a triple and a pair of different ranks.
  for (let t = MIN_PIP; t <= MAX_PIP; t++) {
    const tripleShort = Math.max(0, 3 - (avail[t] as number));
    if (tripleShort > wildCount) continue;
    for (let p = MIN_PIP; p <= MAX_PIP; p++) {
      if (p === t) continue;
      const pairShort = Math.max(0, 2 - (avail[p] as number));
      if (tripleShort + pairShort > wildCount) continue;
      const cards: Card[] = [];
      const used = take(index, t, 3, cards, 0);
      take(index, p, 2, cards, used);
      emit(ComboType.FullHouse, cards, singleOrder(t, level), 0);
    }
  }

  // Straights, and the two run shapes. A run's comparison rank is its last
  // element, which puts the ace-low run below every other run automatically.
  const straightFlushPower = rules.straightFlushRank === "aboveFiveBomb" ? 55 : 65;
  for (const run of runs(5, rules.aceLowRuns)) {
    let short = 0;
    for (const rank of run) short += (avail[rank] as number) > 0 ? 0 : 1;
    const top = run[run.length - 1] as number;

    if (short <= wildCount) {
      const cards: Card[] = [];
      let used = 0;
      for (const rank of run) used = take(index, rank, 1, cards, used);
      emit(ComboType.Straight, cards, top, 0);
    }
    for (let suit = 0 as Suit; suit <= 3; suit = (suit + 1) as Suit) {
      const lane = index.bySuit[suit] as (Card | undefined)[];
      let missing = 0;
      for (const rank of run) if (lane[rank] === undefined) missing += 1;
      if (missing > wildCount) continue;
      const cards: Card[] = [];
      let used = 0;
      for (const rank of run) {
        const card = lane[rank];
        if (card === undefined) cards.push(wilds[used++] as Card);
        else cards.push(card);
      }
      emit(ComboType.StraightFlush, cards, top, straightFlushPower);
    }
  }

  for (const [len, copies, type] of [
    [3, 2, ComboType.ConsecutivePairs],
    [2, 3, ComboType.ConsecutiveTriples],
  ] as const) {
    for (const run of runs(len, rules.aceLowRuns)) {
      let short = 0;
      for (const rank of run) short += Math.max(0, copies - (avail[rank] as number));
      if (short > wildCount) continue;
      const cards: Card[] = [];
      let used = 0;
      for (const rank of run) used = take(index, rank, copies, cards, used);
      emit(type, cards, run[run.length - 1] as number, 0);
    }
  }

  return out;
}

/**
 * Every legal play against the current trick. Passing is always the other option
 * when the trick is live, and is never represented here.
 */
export function legalPlays(
  hand: readonly Card[], current: Combo | null, level: Rank, rules: Ruleset,
): Combo[] {
  const all = enumerateCombos(hand, level, rules);
  if (current === null) return all;
  const out: Combo[] = [];
  for (const combo of all) if (beats(combo, current)) out.push(combo);
  return out;
}
